import { PaginationRequest, RemoveWalletPreferenceKey } from "src/types/commonTypes";
import client from "../client";
import internalClient from "../internalClient";

export const getAllWallets = (limit: number = 10) => {
  return client({
    query: `
    query GetAllWallets($limit: Int) {
      get_all_wallets(limit: $limit) {
        next_token
        wallet {
          balance
          created_by
          description
          display_id
          is_default
          name
          wallet_id
          wallet_status
          wallet_type
        }
      }
    }
    `,
    variables: {
      limit
    }
  });
};

export const getWalletDetail = (walletId: string) => {
  return client({
    query: `
    query GetWallet($input: GetWalletInput) {
      get_wallet(input: $input) {
        preferences {
          is_cash_withdrawal_allowed
          maximum_allowed_limit
          preference_id
          spend_frequency
          spend_limit
          limit_resets_at
          blocked_merchant_ids
          blocked_mcc_categories
          approvers
          verifiers
          require_all_approvers
          require_all_verifiers
          approver_users {
            user_id
            email
            display_id
            attributes {
              first_name
              last_name
            }
          }
          verifier_users {
            user_id
            email
            display_id
            attributes {
              first_name
              last_name
            }
          }
          is_transfer_allowed
          xero_account_id
          xero_tax_type
          gl_code_id
          category_ids
          expenses_since_last_reset
          next_reset_date
          xero_tracking_category_mapping {
            xero_tracking_category_id
            xero_tracking_option_id
          }
        }
        user {
          attributes {
            last_name
            first_name
          }
          user_id
          display_id
          phone
          email
        }
        group {
          group_id
          name
          group_type
        }
        balance
        created_at
        created_by
        description
        display_id
        is_default
        name
        wallet_id
        wallet_status
        wallet_type
      }
    }
    `,
    variables: {
      input: {
        wallet_id: walletId
      }
    }
  });
};

export type CreateWalletInputType = {
  description: string;
  name: string;
  userId: string;
  walletType: string;
  objectType?: string;
};

export const createWallet = (data: CreateWalletInputType) => {
  return client({
    query: `
    mutation WalletMutation($input: CreateWalletInput) {
      create_wallet(input: $input) {
        wallet_id
      }
    }`,
    variables: {
      input: {
        description: data.description,
        name: data.name,
        object_type: data.objectType ?? "USER",
        object_id: data.userId,
        wallet_type: data.walletType
      }
    }
  });
};

export type UpdateWalletInputType = {
  wallet_id: string;
  name: string;
  description: string;
  set_default?: boolean;
};

export const updateWallet = (data: UpdateWalletInputType) => {
  return client({
    query: `
    mutation WalletUpdate($input: UpdateWalletInput) {
      update_wallet(input: $input) {
        balance
        created_by
        description
        display_id
        is_default
        name
        wallet_id
        wallet_status
        wallet_type
      }
    }
    `,
    variables: {
      input: data
    }
  });
};

export const toggleWalletActivation = (walletId: string, isActive: boolean) => {
  return client({
    query: `mutation ToggleWalletActivation($input: ToggleWalletActivationInput) {
      toggle_wallet_activation(input: $input) {
        wallet_status
      }
    }`,
    variables: {
      input: {
        wallet_id: walletId,
        is_active: isActive
      }
    }
  });
};

export const getWalletUsers = (walletId: string) => {
  return client({
    query: `
    query GetWalletUsers($input: WalletQueryInput) {
      get_wallet_users(input: $input) {
        display_id
        email
        is_active
        user_id
        current_organisation_role
        roles {
          organisation_id,
          value
        }
        attributes {
          first_name
          last_name
        }
      }
    }
    `,
    variables: {
      input: {
        wallet_id: walletId
      }
    }
  });
};

export const getWalletCards = (walletId: string) => {
  return client({
    query: `
    query GetWalletCards($input: WalletQueryInput) {
      get_wallet_cards(input: $input) {
        card_setting {
          card_id
          card_setting_status
          expires_at
          sub_type
          ppan
          wallet_id
          card_setting_id 
        }
        holder_name
        card_id
        ppan
        card_status
        card_type
        user_id
        user {
          email
          attributes {
            first_name
            last_name
          }
        }
      }
    }
    `,
    variables: {
      input: {
        wallet_id: walletId
      }
    }
  });
};

export const removeUserFromWallet = (walletId: string, userId: string, fromAll: boolean) => {
  return client({
    query: `
      mutation RemoveUserFromWallet($input: RemoveUserWalletInput) {
        remove_user_from_wallet(input: $input) {
          wallet_id
          user_id
        }
      }    
    `,
    variables: {
      input: {
        all: fromAll,
        wallet_id: walletId,
        user_id: userId
      }
    }
  });
};

export const removeCardFromWallet = (walletId: string, cardId: string, fromAll: boolean) => {
  return client({
    query: `
      mutation RemoveCardFromWallet($input: RemoveCardWalletInput) {
        remove_card_from_wallet(input: $input) {
          card_id
          wallet_id
        }
      }    
    `,
    variables: {
      input: {
        all: fromAll,
        wallet_id: walletId,
        card_id: cardId
      }
    }
  });
};

export const connectUserToWallet = (walletId: string, userId: string, toAll: boolean = false) => {
  return client({
    query: `
    mutation ConnectUserToWallet($input: ConnectUserWalletInput) {
      connect_user_to_wallet(input: $input) {
        card_id
        user_id
        wallet_id
      }
    }
    
    `,
    variables: {
      input: {
        all: toAll,
        wallet_id: walletId,
        user_id: userId
      }
    }
  });
};

export const getConnectedCardWallets = (cardId: string) => {
  return client({
    query: `
    query GetConnectedCardWallets($input: CardQueryInput) {
      get_connected_card_wallets(input: $input) {
        wallet_id
        name
        description
        wallet_type
        wallet_status
        balance
        created_by
        display_id
        is_default
        user {
          email
          user_id
          display_id
          attributes {
            first_name
            last_name
          }
        }
        group {
          group_id
          name
          group_type
        }
      }
    }`,
    variables: {
      input: {
        card_id: cardId
      }
    }
  });
};

export const connectCardToWallet = (walletId: string, cardId: string, toAll: boolean = false) => {
  return client({
    query: `
    mutation AddCardToWallet($input: AddCardWalletInput) {
  add_card_to_wallet(input: $input) {
        card_id
        wallet_id
      }
    }
    `,
    variables: {
      input: {
        all: toAll,
        wallet_id: walletId,
        card_id: cardId
      }
    }
  });
};

export interface SearchWalletRequest extends PaginationRequest {
  walletFilter?: {
    wallet_status?: string;
    wallet_types?: string[];
    balance?: {
      min?: number;
      max?: number;
    };
    created_at?: {
      min?: string;
      max?: string;
    };
    wallet_ids?: string[];
    user_ids?: string[];
    display_id?: string;
    name?: string;
    object_type?: string;
    xero_account_exist?: boolean;
    xero_tracking_category_exist?: boolean;
  };
  sort_field?: {
    wallet?: "balance" | "created_at";
  };
  sort_by?: "asc" | "desc";
  userFilter?: {
    email?: string;
  };
  orgFilter?: {
    organisationDisplayId?: string;
  };
}

export const searchWallet = (data?: SearchWalletRequest) => {
  const input: any = {
    from: data?.from,
    size: data?.size,
    filter: {
      organisation_filter: {
        display_id: data?.orgFilter?.organisationDisplayId
      },
      wallet_filter: data?.walletFilter,
      user_filter: data?.userFilter
    }
  };

  if (data?.sort_field && data?.sort_by) {
    input.sort_field = data.sort_field;
    input.sort_by = data.sort_by;
  }

  return client({
    query: `
    query SearchWallet($input: SearchInput) {
      search_wallet(input: $input) {
        total_pages
        total_size
        items {
          balance
          created_at
          created_by
          description
          display_id
          is_default
          name
          wallet_id
          wallet_status
          wallet_type
          preference_attributes {
            is_cash_withdrawal_allowed
            maximum_allowed_limit
            preference_id
            spend_frequency
            limit_resets_at
            spend_limit
            blocked_merchant_ids
            blocked_mcc_categories
            approvers
            verifiers
            is_transfer_allowed
            expenses_since_last_reset
            next_reset_date
            xero_account_id
            xero_tax_type
            gl_code_id
            category_ids
            xero_tracking_category_mapping {
              xero_tracking_category_id
              xero_tracking_option_id
            }
          }
          user {
            attributes {
              last_name
              first_name
            }
            email
            user_id
            display_id
          }
          group {
            group_id
            name
            group_type
          }
        }
      }
    }`,
    variables: {
      input
    }
  });
};

export const InternalSearchWallet = (data?: SearchWalletRequest) => {
  return internalClient({
    query: `
    query SearchWallet($input: SearchInput) {
      internal_search_wallet(input: $input) {
        total_pages
        total_size
        items {
          balance
          created_at
          created_by
          description
          display_id
          organisation_id
          is_default
          name
          wallet_id
          wallet_status
          wallet_type
          user {
            attributes {
              last_name
              first_name
            }
            email
            user_id
            display_id
          }
          group {
            group_id
            name
            group_type
          }
        }
      }
    }`,
    variables: {
      input: {
        from: data?.from,
        size: data?.size,
        filter: {
          organisation_filter: {
            display_id: data?.orgFilter?.organisationDisplayId
          },
          wallet_filter: data?.walletFilter,
          user_filter: data?.userFilter
        }
      }
    }
  });
};

export const toggleDefaultWallet = (walletId: string) => {
  return client({
    query: `
    mutation ToggleDefaultWallet($input: SetDefaultWalletInput) {
      set_default_wallet(input: $input) {
        is_default
        display_id
        wallet_id 
      }
    }`,
    variables: {
      input: {
        wallet_id: walletId
      }
    }
  });
};

export type WalletPreferenceRequest = {
  cashWithdrawalAllowed: boolean;
  maxAllowedLimit: number;
  spendFrequency: string;
  limit_resets_at: string;
  spendLimit: number;
  walletId: string;
  blockedMerchantIds: string[];
  blockedMccCategories: string[];
  approvers: string[];
  verifiers: string[];
  // Left undefined, the backend keeps the stored value.
  requireAllApprovers?: boolean;
  requireAllVerifiers?: boolean;
  isTransferAllowed: boolean;
  xero_account_id?: string;
  xero_tax_type?: string;
  xero_tracking_category_mapping?: {
    xero_tracking_category_id: string;
    xero_tracking_option_id?: string;
  }[];
  gl_code_id?: string;
  category_ids?: string[];
};

export const changeWalletPreference = (data: WalletPreferenceRequest) => {
  return client({
    query: `
    mutation ChangeWalletPreference($input: WalletPreferencesInput) {
      save_wallet_preferences(input: $input) {
        is_cash_withdrawal_allowed
        maximum_allowed_limit
        preference_id
        spend_frequency
        limit_resets_at
        spend_limit
        blocked_merchant_ids
        blocked_mcc_categories
        approvers
        verifiers
        require_all_approvers
        require_all_verifiers
        is_transfer_allowed
        xero_account_id
        xero_tax_type
        gl_code_id
        category_ids
        xero_tracking_category_mapping {
          xero_tracking_category_id
          xero_tracking_option_id
        }
      }
    }`,
    variables: {
      input: {
        spend_frequency: data.spendFrequency ?? "WEEKLY",
        limit_resets_at: data.limit_resets_at ?? "1",
        spend_limit: data.spendLimit ?? 1000,
        is_cash_withdrawal_allowed: data.cashWithdrawalAllowed ?? false,
        maximum_allowed_limit: data.maxAllowedLimit ?? 1000,
        wallet_id: data.walletId,
        blocked_merchant_ids: data.blockedMerchantIds ?? [],
        blocked_mcc_categories: data?.blockedMccCategories ?? [],
        approvers: data?.approvers ?? [],
        verifiers: data?.verifiers ?? [],
        require_all_approvers: data?.requireAllApprovers,
        require_all_verifiers: data?.requireAllVerifiers,
        is_transfer_allowed: data?.isTransferAllowed ?? true,
        xero_account_id: data?.xero_account_id,
        xero_tax_type: data?.xero_tax_type,
        xero_tracking_category_mapping: data?.xero_tracking_category_mapping,
        gl_code_id: data?.gl_code_id,
        category_ids: data?.category_ids
      }
    }
  });
};

export const removeWalletPreference = (wallet_id: string, key: RemoveWalletPreferenceKey) => {
  return client({
    query: `
    mutation RemoveWalletPreference($input: RemoveWalletPreferencesInput) {
      remove_key_from_wallet_preference(input: $input) {
        is_cash_withdrawal_allowed
        maximum_allowed_limit
        preference_id
        spend_frequency
        spend_limit
        blocked_merchant_ids
        blocked_mcc_categories
        is_transfer_allowed
        xero_account_id
        xero_tax_type
        xero_tracking_category_mapping {
          xero_tracking_category_id
          xero_tracking_option_id
        }
      }
    }`,
    variables: {
      input: {
        wallet_id,
        key
      }
    }
  });
};

export interface WalletDownloadRequest {
  walletFilter?: {
    wallet_status?: string;
    wallet_type?: string;
    balance?: {
      min?: number;
      max?: number;
    };
    created_at?: {
      min?: string;
      max?: string;
    };
    user_ids?: string[];
    name?: string;
    custom_organisation_id?: string;
    xero_account_exist?: boolean;
    xero_tracking_category_exist?: boolean;
  };
  columns?: string[];
  timeZone: string;
  report_name: string;
}

export const downloadWallet = (data: WalletDownloadRequest) => {
  return client({
    query: `query download_wallets ($input: DownloadReportInput) {
      download_wallets(input: $input) {
        report_id
      }
    }`,
    variables: {
      input: {
        filter: {
          wallet_filter: data?.walletFilter
        },
        payload: {
          filter: {
            wallet_filter: data?.walletFilter
          },
          from: 0,
          size: 10000
        },
        timezone: data?.timeZone,
        columns: data?.columns,
        report_name: data?.report_name
      }
    }
  });
};

export const downloadInternalWallet = (data: WalletDownloadRequest) => {
  return internalClient({
    query: `query download_wallets ($input: DownloadReportInput) {
      internal_download_wallets(input: $input) {
        report_id
      }
    }`,
    variables: {
      input: {
        filter: {
          wallet_filter: data?.walletFilter
        },
        payload: {
          filter: {
            wallet_filter: data?.walletFilter
          },
          from: 0,
          size: 10000
        },
        timezone: data?.timeZone,
        columns: data?.columns,
        report_name: data?.report_name
      }
    }
  });
};

export type WalletStatementPeriod = "THIS_MONTH" | "LAST_MONTH" | "CUSTOM";
export type ReportFileFormat = "PDF" | "CSV";

export interface DownloadWalletStatementRequest {
  wallet_id: string;
  period: WalletStatementPeriod;
  from?: string;
  to?: string;
  format: ReportFileFormat;
  timezone: string;
}

export const downloadWalletStatement = (data: DownloadWalletStatementRequest) => {
  return client({
    query: `mutation DownloadWalletStatement($input: DownloadWalletStatementInput) {
      download_wallet_statement(input: $input) {
        report_id
      }
    }`,
    variables: { input: data }
  });
};

export interface createRecurringTransferRequest {
  source_wallet_id: string;
  destination_wallet_id: string;
  amount: number;
  frequency: string;
  start_at: string;
  type: string;
}

export const createRecurringTransfer = (data: createRecurringTransferRequest) => {
  return client({
    query: `
      mutation CreateRecurringTransfer($input: CreateRecurringWalletTransactionInput) {
        create_recurring_wallet_transaction(input: $input) {
          recurring_transaction_id
        }
      }
    `,
    variables: {
      input: data
    }
  });
};

export interface updateRecurringTransferRequest {
  recurring_transaction_id?: string;
  amount?: number;
  // frequency?: string;
  // limit_resets_at?: string;
  type?: string;
  is_active?: boolean;
}

export const updateRecurringTransfer = (data: updateRecurringTransferRequest) => {
  return client({
    query: `
      mutation UpdateRecurringTransfer($input: UpdateRecurringWalletTransactionInput) {
        update_recurring_wallet_transaction(input: $input) {
          recurring_transaction_id
        }
      }
    `,
    variables: {
      input: data
    }
  });
};

export interface SearchRecurringTransferRequest extends PaginationRequest {
  filter: {
    recurring_transaction_filter: {
      source_wallet_id?: string;
      type?: string;
      is_active?: boolean;
    };
  };
}

export const searchRecurringTransfer = (data: SearchRecurringTransferRequest) => {
  return client({
    query: `
      query SearchRecurringTransaction($input: SearchInput) {
        search_recurring_transaction(input: $input) {
          total_pages
          total_size
          items {
            destination_wallet {
              name
              wallet_id
              description
            }
            source_wallet {
              name
              wallet_id
              description
            }
            recurring_transaction_id
            next_run_at
            last_run_at
            frequency
            start_at
            amount
            created_at
            updated_at
            status
            type
          }
        }
      }
    `,
    variables: {
      input: data
    }
  });
};
